import type { MouseEvent } from 'react';
import { useState } from 'react';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as quoteSelectors from '@makemydeal/dr-dash-store/selectors/quoteSelectors';
import { DeskingModeCTAText, DeskingModeCTAValue, DealMode } from '@makemydeal/dr-dash-store/types/dealModeEnum';
import { useChangeDealMode } from '@makemydeal/dr-dash-store/hooks/useChangeDealMode';

import { useSelector, useDispatch } from 'react-redux';
import { clearSaveValidation, validateSaveState } from '@makemydeal/dr-dash-store/actions/saveValidationActionCreators';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { ToggleGroup } from '@interstate/components/ToggleGroup';
import { DeskingFnISwitchErrorsModal } from '../modals/components/DeskingFnISwitchErrorsModal';

type DeskingModeCTAProps = {
    toggleWidth?: string;
};

export const DeskingModeCTA = ({ toggleWidth = '180px' }: DeskingModeCTAProps) => {
    const dispatch = useDispatch();
    const isDealCentralWithFnI = useSelector(dealerSelectors.isDealCentralWithFnI);
    const isFnIDealMode = useSelector(dealModeSelectors.isFnIDealMode);
    const isQuoteMode = useSelector(quoteSelectors.getQuoteMode);
    const { handleChangeDealMode } = useChangeDealMode();
    const [showFnISwitchModal, setShowFnISwitchModal] = useState(false);
    const [pendingMode, setPendingMode] = useState<DeskingModeCTAValue | null>(null);
    const openFnISwitchModal = () => setShowFnISwitchModal(true);
    const closeFnISwitchModal = () => {
        setShowFnISwitchModal(false);
        setPendingMode(null);
    };

    const handleModeToggle = (_e: MouseEvent, value: DeskingModeCTAValue) => {
        if (value === DeskingModeCTAValue.FnIMode) {
            // A quote has no shopper by definition, so buyer rules would fail on every single switch.
            // Saving and pushing downstream are still gated on buyer by the save/exit validation guards.
            const { hasErrors } = dispatch(
                validateSaveState({ skipValidationKeys: isQuoteMode ? ['buyer', 'vehicle'] : ['vehicle'] })
            );
            if (hasErrors) {
                setPendingMode(value);
                openFnISwitchModal();
                return;
            }
        }
        handleChangeDealMode(value);
    };

    const handleFnISwitchContinue = () => {
        closeFnISwitchModal();
        dispatch(clearSaveValidation());
        if (pendingMode !== null) {
            handleChangeDealMode(pendingMode);
            setPendingMode(null);
        }
    };

    if (!isDealCentralWithFnI) {
        return null;
    }

    return (
        <>
            <DeskingFnISwitchErrorsModal
                show={showFnISwitchModal}
                onClose={closeFnISwitchModal}
                onContinue={handleFnISwitchContinue}
            />
            <ToggleGroup
                data-testid="desking-mode-cta"
                onChange={handleModeToggle}
                toggleButtonOptions={[
                    {
                        selected: !isFnIDealMode,
                        'data-testid': DealMode.deskingMode,
                        'aria-label': 'Desking',
                        children: 'Desking',
                        value: DeskingModeCTAValue.DeskingMode
                    },
                    {
                        selected: isFnIDealMode,
                        'data-testid': DeskingModeCTAText.FnIMode,
                        'aria-label': 'F&I',
                        children: 'F&I',
                        value: DeskingModeCTAValue.FnIMode
                    }
                ]}
                toggleWidth={toggleWidth}
                size="sm"
            />
        </>
    );
};
